import { useHotkeys } from 'react-hotkeys-hook';

import { useFullscreenContext } from '~/components/AssetModalV2/shared/context/FullscreenContext';

let _stopEscKeyPropagation = false;
const _assetModalEscKeyHandlers: Array<() => void> = [];

export const stopEscKeyPropagationOnAssetDetailsModal = (nextValue: boolean) => {
  _stopEscKeyPropagation = nextValue;
};

export const registerAssetModalEscKeyHandler = (handler: () => void) => {
  _assetModalEscKeyHandlers.push(handler);

  return () => {
    const handlerIndex = _assetModalEscKeyHandlers.lastIndexOf(handler);

    if (handlerIndex !== -1) {
      _assetModalEscKeyHandlers.splice(handlerIndex, 1);
    }
  };
};

export type UseAssetModalEscKeyHandlerProps = {
  onClose: () => void;
};

/**
 * Open menu, select, and popover content that actually closes on Escape.
 * A tooltip is role="tooltip". A popover that stays open omits data-dismisses-on-escape.
 */
const OPEN_ESCAPE_OVERLAY = [
  '[role="menu"][data-state="open"]',
  '[role="listbox"][data-state="open"]',
  '[role="dialog"][data-state="open"][data-dismisses-on-escape="true"]',
].join(', ');

export const useAssetModalEscKeyHandler = ({ onClose }: UseAssetModalEscKeyHandlerProps) => {
  const { isFullscreen } = useFullscreenContext();
  const closeAssetModal = onClose;

  useHotkeys(
    'esc',
    (event) => {
      // These layers call preventDefault and do not stop propagation. A tooltip, and a
      // popover that stays open, do the same and must not skip a registered handler.
      if (event.defaultPrevented && document.querySelector(OPEN_ESCAPE_OVERLAY)) {
        return;
      }

      if (_stopEscKeyPropagation) {
        return;
      }

      const registeredHandler = _assetModalEscKeyHandlers[_assetModalEscKeyHandlers.length - 1];

      if (registeredHandler) {
        registeredHandler();
        return;
      }

      closeAssetModal();
    },
    {
      enabled: !isFullscreen,
    },
    [closeAssetModal],
  );
};
